import { discountFraction } from "@/core/discount";

const DAY = 24 * 60 * 60 * 1000;
/** How far ahead a scheduled retirement starts showing on model pages. */
export const RETIREMENT_NOTICE_DAYS = 90;

interface Retirable {
	deactivatedAt?: Date | string | null;
}

/** Retired only once the date has passed; a future date is just scheduled. */
export function isMappingDeactivated(mapping: Retirable, now = new Date()) {
	return Boolean(
		mapping.deactivatedAt && new Date(mapping.deactivatedAt) <= now,
	);
}

/** Retired, or retiring within the notice window. Display only: never filter on it. */
export function shouldShowDeactivationNotice(
	mapping: Retirable,
	now = new Date(),
	withinDays = RETIREMENT_NOTICE_DAYS,
) {
	if (!mapping.deactivatedAt) {
		return false;
	}
	const at = new Date(mapping.deactivatedAt).getTime();
	return at <= now.getTime() || at - now.getTime() <= withinDays * DAY;
}

/** The mapping a model page opens on: global first, then region-less, then any. */
export function getDefaultProviderMapping<T extends { region?: string | null }>(
	mappings: T[],
): T {
	return (
		mappings.find((mapping) => mapping.region === "global") ??
		mappings.find((mapping) => !mapping.region) ??
		(mappings[0] as T)
	);
}

/** "$0.04" for per-second and per-image prices, to at most five decimals. */
export function formatPerUnitPrice(value: number): string {
	return `$${Number(value.toFixed(5))}`;
}

const PRICE = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/;

/** Cheapest per-second price after the mapping's discount, or null. */
export function getMinPerSecondPrice(mapping: {
	perSecondPrice?: Record<string, string | number> | null;
	discount?: string | null;
}): number | null {
	if (!mapping.perSecondPrice) {
		return null;
	}
	const off = discountFraction(mapping.discount);
	const prices = Object.values(mapping.perSecondPrice)
		.map((value) => String(value).trim())
		.filter((value) => PRICE.test(value))
		.map((value) => Number((Number(value) * (1 - off)).toFixed(5)));
	return prices.length ? Math.min(...prices) : null;
}
